import {ContextMenu, Icon, Li, Loading, Tooltip} from '@startlibs/components';
import {ToggleCheckbox, RadioboxGroup, TabRadiobox, TextInput} from '@startlibs/form'
import {
  addQueryString, callIfFunction, formatDate,
  getColor,
  getFetcher,
  media,
  postFetcher,
  isMobile, smoothScroll
} from '@startlibs/utils'
import {navigate} from '@reach/router'
import {useConstant, Slot, usePopupToggle, useToggle, Fill, useRefState} from '@startlibs/core'
import React, {Suspense, useEffect, useState, useMemo, useImperativeHandle, useRef} from 'react'
import _ from 'lodash/fp'
import styled, {css} from 'styled-components'

import {ALL_CASES, OPEN_CASES, REPORTED_CASES} from '../enums/StudyStatus'
import {ActiveAdvancedFilters} from './worklist/ActiveAdvancedFilters'
import {AdvancedSearchPopup} from './worklist/AdvancedSearchPopup'
import {Pagination} from '../components/Pagination'
import {PurviewFooter} from '../components/PurviewFooter'
import {SectionHeading} from '../components/PageLayout';
import {Table} from './worklist/Table';
import {stickyOnDesktop} from './worklist/stickyOnDesktop';
import {willUseSuspense} from '../hooks/useSuspense'
import {callUpdateParams, updateParams, useRegisterSetParams, useUpdateParams} from '../hooks/useUpdateParams'
import {getDate, getDateYear, isIOs, isMac, searchToParams} from '../utils/utils'
import {useNotificationRef} from '../components/Notifications'
import {FetchErrorDialog} from './errors/FetchErrorDialog'
import {AsyncDownloadManager} from '../components/AsyncDownloadManager'
import {cachedWorklistFetcher} from './utils/cachedWorklistFetcher'
import {
  isPastYearToggleDisabled,
  isUsingStudyPastYear,
  isUsingUploadedPastYear,
  useRegisterIsOrderingBy
} from '../hooks/useIsOrderingBy'
import {AcceptTermsDialog} from './worklist/AcceptTermsDialog'
import {lazyStudyUserRights} from '../lazy/lazyStudyUserRights'

let authenticated = process.env.NODE_ENV !== 'development'

const WorklistPageContainer = styled.div`
  padding: 1rem 0rem 90px;
  margin: 0 auto;
  position: relative;
  min-height: 100vh;
  ${props => !props.isMobile && css`
    min-width: ${props => props.visibleColumnsAmount > 8 ? '115rem' : '100rem'};
    max-width: ${props => props.visibleColumnsAmount > 8 ? '135rem' : '115rem'};
  `}
  ${props => props.isMobile && css`
    min-width: 300px;
  `}
  p {
    font-size: 13px;
  }
`
const FooterContainer = styled.div`
  ${props => !props.isMobile && css`
    min-width: ${props => props.visibleColumnsAmount > 8 ? '115rem' : '100rem'};
  `}
`
const WorklistPurviewFooter = styled(PurviewFooter)`
  ${stickyOnDesktop()}
  margin-left: 0;
  margin-right: 0;
`
const WorklistCard = styled.div`
  & ~ ${SectionHeading} {
    margin-top: 3rem;
  }
  ${media.desktop`
    padding: 2.5rem;
    background: white;
    border-radius: 8px;
    border: 1px solid rgba(0, 0, 0, 0.15);
    margin: 0 auto;
  `}
`
const WorklistSectionHeading = styled(SectionHeading)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  min-height: 3rem;
  h3 {
    margin-bottom: 0;
  }
  ${stickyOnDesktop()};
  ${media.mobile`
    padding-left: 1rem;
    padding-right: 1rem;
  `}
  ${props => props.isMobile && css`
    padding-left: 1rem;
    padding-right: 1rem;
  `}
`

const WorklistHeaderComponent = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 2rem;
  z-index: 30;
  .right-wrapper {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    position: relative;
    flex-basis: 560px;
  }
  ${media.max(915)`
    flex-wrap: wrap;
    .right-wrapper {
      flex-grow: 1;
      flex-wrap: wrap;
      flex-direction: column-reverse;
      align-items: flex-end;
      ${RadioboxGroup} {
        margin-bottom: 1rem;
      }
    }
  `}
  ${media.min(916)`
    ${RadioboxGroup} {
      position: sticky;
      right: 2.5rem;
      max-width: 340px;
      margin-left: 1rem;
    }
  `}
  ${media.max(915)`
    display: block;
    .right-wrapper {
      padding-top: 1.5rem;
      margin-bottom: -0.5rem;
      flex-wrap: nowrap;
      flex-direction: row;
      ${RadioboxGroup} {
        width: auto;
        flex-basis: 0;
        flex-grow: 1;
        margin-bottom: 0;
      }
    }
  `}
  ${media.max(515)`
    .right-wrapper {
      flex-basis: 100%;
      flex-wrap: wrap;
      flex-direction: column;
      align-items: stretch;
      ${RadioboxGroup} {
        flex-basis: auto;
      }
    }
  `}
  ${media.mobile`
    padding: 0 1rem;
  `}
  ${stickyOnDesktop()}
`

const PastYearCheckbox = styled(ToggleCheckbox)`
  margin-bottom: 0;
  margin-left: 1rem;
  white-space: nowrap;
  min-width: 195px;
  ${props => props.disabled && css`
    color: ${getColor('gray150')};
  `}
  ${media.max(915)`
    margin: 0 1rem 0 0;
  `}
  ${media.max(601)`
    padding: 13px 1rem;
  `}
  ${media.max(515)`
    margin: 0 0 1rem 0;
    flex-grow: 1;
  `}
  ${media.max(450)`
    position: absolute;
    top: -4.25rem;
    right: 0;
  `}
  ${media.max(350)`
    position: static;
  `}
`

const LinkButton = styled.a`
  position: relative;
  display: inline-block;
  text-decoration: underline;
  user-select: none;
`
const SearchInputWrapper = styled.div`
  display: flex;
  align-items: center;
  color: ${getColor('gray120')};
  ${media.max(450)`
    display: block;
  `}
`
const SearchInput = styled.div`
  position: relative;
  display: inline-block;
  margin-right: 1rem;
  max-width: 30rem;
  width: 100%;
  ${media.max(450)`
    margin-right: 0;
    margin-bottom: .5rem;
    max-width: 100%;
  `}
  ${LinkButton} {
    position: absolute;
    right: 1rem;
    top: 50%;
    transform: translateY(-50%);
  }
  > input {
    word-break: normal;
    padding-right: 8.75rem;
  }
  ${props => props.expandedInput && `
    > input {
      border-bottom-left-radius: 0px;
      border-bottom-right-radius: 0px;
    }
  `}
`
const ClearSearchButton = styled(Icon)`
  color: ${getColor('gray90')};
  font-size: 12px;
  position: absolute;
  right: 80px;
  top: 50%;
  transform: translateY(-50%);
  cursor: pointer;
  width: 1.5rem;
  line-height: 1.5rem;
  text-align: center;
  border-radius: 50%;
  background: rgba(0,0,0,0.1);
  :hover {
    background: #f7d3d4;
    color: ${getColor('alert')};
  }
`

const PaginationContainer = styled.div`
  text-align: center;
  margin-top: 2rem;
  ${stickyOnDesktop()}
`

const MaxResultsMenu = styled(ContextMenu)`
  li > * {
    padding: 11px 2rem;
  }
`
const LoadingWorklist = styled.div`
  border-radius: 5px;
  height: 26rem;
  ${stickyOnDesktop()}
`

export const Worklist = ({location}) => {
  const userRightsOptions = lazyStudyUserRights.read()
  const options = userRightsOptions.options
  const [rawUserRights, rawSetUserRights] = useState(userRightsOptions.userRights)
  const userRights = _.flow(
    (isMac) ? _.identity : _.set('tools.functions.launchInHoros', -1),
    _.unset('tools.functions.orderBy'),
    _.unset('tools.functions.lastYearFilter'),
    _.unset('tools.functions.tabCasesDefault'),
    options.viva_lite ? _.unset('tools.functions.viewStudy') : _.identity,
    options.viva_lite ? _.unset('tools.functions.viewerLink') : _.identity,
  )(rawUserRights)
  useRegisterIsOrderingBy(() => rawUserRights.tools.functions.orderBy)

  const tabCasesDefault = (rawUserRights.tools.functions.tabCasesDefault+"") || OPEN_CASES

  const needsAccepTerms =  useToggle(!options.accepted_terms && options.blankWorklist)
  // const needsAccepTerms =  useToggle(true)
  // userRights.performingPhysician = {visible:true}

  const setUserRights = (setter) => {
    const previousTools = rawUserRights.tools.functions
    const updatedTools = setter(previousTools)
    console.log(updatedTools)
    postFetcher('/StudyWorklistOptions.php', updatedTools)
    rawSetUserRights(_.set('tools.functions', updatedTools))
  }


  const visibleColumnsAmount = useMemo(() => {
    return _.sum(_.toPairs(userRights).map(([key, {visible}]) => visible && key !== 'reportWriteAccess' ? (key === "study" ? 2 : 1) : 0))
  }, [])

  const loading = useToggle()
  const [params, rawSetParams] = useState(() => _.set('pastYear', !!rawUserRights.tools.functions.lastYearFilter, searchToParams(location.search)))

  const updateParams = (params) => {
    rawSetParams(prevParams => _.set('pastYear', prevParams.pastYear, params))
    callUpdateParams(params)
  }

  useEffect(() => {
    updateParams(searchToParams(location.search))
  }, [location])

  useEffect(() => {
    if (!!rawUserRights.tools.functions.lastYearFilter !== params.pastYear) {
      setUserRights(_.set('lastYearFilter', params.pastYear ? 1 : 0))
    }
  }, [params])


  const debounceSetParams = useConstant(_.debounce(500, rawSetParams))

  const setParams = (updater, debounce) => {
    const verifyPage = (updater) => (state) => {
      const updatedState = callIfFunction(updater, state)
      if (
        state.search !== updatedState.search ||
        state.studyStatus !== updatedState.studyStatus ||
        state.dateOfBirth !== updatedState.dateOfBirth ||
        state.studyDate !== updatedState.studyDate ||
        state.uploadDateInit !== updatedState.uploadDateInit ||
        state.uploadDateEnd !== updatedState.uploadDateEnd ||
        state.pastYear !== updatedState.pastYear
      ) {
        return _.flow(
          _.set('page', 1),
          _.unset('forceAll')
        )(updatedState)
      } else if (state.maxResults !== updatedState.maxResults) {
        const firstRow = ((state.maxResults || 10) * (updatedState.page - 1) + 1)
        return _.set('page', Math.max(Math.floor(firstRow / (updatedState.maxResults || 10)) + 1, 1), updatedState)
      } else {
        return updatedState
      }
    }
    if (debounce) {
      debounceSetParams(verifyPage(updater))
    } else {
      setTimeout(() => rawSetParams(verifyPage(updater)), 0)
    }
    setTimeout(loading.open, 0)
  }
  useRegisterSetParams(setParams)

  return <>
    <WorklistPageContainer visibleColumnsAmount={visibleColumnsAmount} isMobile={isMobile()}>
      <WorklistSectionHeading isMobile={isMobile()}>
        <h3>Study Browser</h3>
        <Slot name="AddStudiesButton"/>
      </WorklistSectionHeading>
      <WorklistCard>
        <WorklistHeader
          allTabs={options.allTabs}
          params={params}
          tabCasesDefault={tabCasesDefault}
          setParams={setParams}
          location={location}
          loading={loading}
        />
        <Suspense fallback={null}>
          <WorklistTable
            tabCasesDefault={tabCasesDefault}
            blankWorklist={options.blankWorklist}
            allTabs={options.allTabs}
            updateParams={updateParams}
            loading={loading} params={params} setParams={setParams} location={location}
            userRights={userRights}
            setUserRights={setUserRights}
          />
        </Suspense>
      </WorklistCard>
      <AsyncDownloadManager/>
    </WorklistPageContainer>
    <FooterContainer
      isMobile={isMobile()}
      visibleColumnsAmount={visibleColumnsAmount}
    ><WorklistPurviewFooter/></FooterContainer>
    {
      needsAccepTerms.isOpen &&
      <AcceptTermsDialog closeDialog={needsAccepTerms.close}/>
    }
  </>
}

const WorklistHeader = ({location, allTabs, loading, params, setParams, tabCasesDefault}) => {

  const query = useToggle(params.search || '')
  const status = useToggle(params.studyStatus || tabCasesDefault)
  const maxResults = useToggle(params.maxResults || '')

  const updateUrl = useConstant(
    _.throttle(200, (obj) => {
      window.top.history.pushState('', '', ('?' + addQueryString('', _.pickBy((value, key) => key !== "pastYear" && value, obj))).replace(/\?\?/, '?'))
    })
  )

  useUpdateParams((params) => {
    query.openWith(params.search || "")
    status.openWith(params.studyStatus || tabCasesDefault)
    maxResults.openWith(params.maxResults || "")
  })

  useEffect(() => {
    updateUrl(params)
  }, [params])


  const maxResultsMenu = usePopupToggle()
  const advancedPopup = useToggle()

  const updateSearch = (v) => {
    query.openWith(v)
    setParams(_.set('search', v), true)
  }

  const updateStatus = (v) => {
    status.openWith(v)
    setParams(_.set('studyStatus', v), true)
  }
  const updateMaxResult = (v) => {
    maxResults.openWith(v)
    setParams(_.set('maxResults', v), true)
  }

  return <WorklistHeaderComponent>
    <div css="flex-grow:1;">
      <SearchInputWrapper>
        <SearchInput expandedInput={advancedPopup.isOpen}>
          <TextInput raw value={query.isOpen} setValue={updateSearch} placeholder="Search cases"/>
          {query.isOpen && <ClearSearchButton icon="x" onClick={() => updateSearch("")}/>}
          <LinkButton onClick={advancedPopup.open}>
            Advanced
          </LinkButton>
          {
            advancedPopup.isOpen &&
            <AdvancedSearchPopup
              focusedInput={advancedPopup.isOpen}
              closePopup={advancedPopup.close}
              setParams={setParams}
              params={params}
            />
          }
        </SearchInput>
        <div>
          <Slot name="QueryCount"/>
          <div className="nowrap">Showing <LinkButton onClick={maxResultsMenu.open}>{(maxResults.isOpen || 10)} per page
            {
              maxResultsMenu.isOpen &&
              <MaxResultsMenu offset="0 4">
                <Li label="10" onClick={() => updateMaxResult('')}/>
                <Li label="25" onClick={() => updateMaxResult(25)}/>
                <Li label="50" onClick={() => updateMaxResult(50)}/>
              </MaxResultsMenu>
            }
          </LinkButton>
          </div>
        </div>
      </SearchInputWrapper>
      <ActiveAdvancedFilters params={params} setParams={setParams} advancedPopup={advancedPopup} location={location}/>
    </div>
    <div className="right-wrapper">
      <PastYearToggle params={params} setParams={setParams}/>
      {
        allTabs &&
        <RadioboxGroup wide css="a{white-space:nowrap;padding-left:1rem;padding-right:1rem;}">
          <TabRadiobox
            raw label="Open cases" fieldValue={status.isOpen === '' ? '' : OPEN_CASES} value={status.isOpen} white={isMobile()}
            setValue={updateStatus}
          />
          <TabRadiobox
            raw label="Reported cases" fieldValue={REPORTED_CASES} value={status.isOpen} white={isMobile()}
            setValue={updateStatus}
          />
          <TabRadiobox
            raw label="All cases" fieldValue={ALL_CASES} value={status.isOpen} white={isMobile()}
            setValue={updateStatus}
          />
        </RadioboxGroup>
      }
    </div>
  </WorklistHeaderComponent>
}


const PastYearToggle = ({params, setParams}) => {
  const value = isUsingStudyPastYear(params) || isUsingUploadedPastYear(params)
  const isDisabled = isPastYearToggleDisabled(params)
  const setValue = (v) => setParams(_.set('pastYear', v))

  const toggle = <PastYearCheckbox
    raw
    label="Display last 12 months only"
    value={value}
    disabled={isDisabled}
    setValue={setValue}
  />

  return isDisabled ? <Tooltip content="This limit is disabled while filtering.">{toggle}</Tooltip> :
    <Tooltip content="Pages load faster if enabled.">{toggle}</Tooltip>
}

const FILTER_LBABEL = {
  1: "",
  "": "open",
  2: "open",
  3: "reported",
}

const WorklistTable = React.memo(({tabCasesDefault, params, allTabs, blankWorklist, updateParams, loading, setParams, ...props}) => {

  const [worklist, setWorklist] = useState()
  const lastParams = useRefState(params)
  const lastSuccessParams = useRefState(params)
  const errorDialog = useToggle()


  const maxResults = Number(params.maxResults) || 10
  const currentPage = Number(params.page) || 1

  const loadList = useConstant((currentParams, closeLoading, refreshing) => {
    lastParams.set(currentParams)
    if (lastParams.get() === lastSuccessParams.get() && !refreshing) {
      callIfFunction(closeLoading)
      return Promise.resolve()
    }
    if (blankWorklist && (currentParams.search||"").length < 3) {
      lastSuccessParams.set(currentParams)
      window.scroll(0, window.pageYOffset);
      smoothScroll(0)
      setWorklist({worklist:{blankingWorklist:true, dbCount:0,rows:[]}, key: Date.now()})
      callIfFunction(closeLoading)
      return Promise.resolve()
    }
    const lastYearDate = formatDate(getDateYear(-1), "yyyy-MM-dd")
    const todayDate = formatDate(getDate(0), "yyyy-MM-dd")
    const filteringYearDates = isUsingUploadedPastYear(currentParams)
      ? {uploadDateInit: lastYearDate, uploadDateEnd: todayDate}
      : isUsingStudyPastYear(currentParams)
        ? {studyDate: lastYearDate, stuydDateEnd: todayDate}
        : {}
    return cachedWorklistFetcher({
      page: 1,
      maxResults: 10,
      studyStatus: allTabs ? tabCasesDefault : ALL_CASES, ...filteringYearDates, ..._.pickBy(_.identity, currentParams)
    }, refreshing)
      // return getFetcher(addQueryString('/StudyWorklist.php?', {maxResults: 10, page: 1, studyStatus: OPEN_CASES, ..._.pickBy(_.identity,currentParams)}))
      .then(({worklist}) => {
          if (lastParams.get() === currentParams) {
            lastSuccessParams.set(currentParams)
            window.scroll(0, window.pageYOffset);
            smoothScroll(0)
            setWorklist({worklist, key: Date.now()})
            callIfFunction(closeLoading)
          }
        }
      )
      .catch(() => {
        if (lastParams.get() === currentParams) {
          callIfFunction(closeLoading)
          errorDialog.open()
          updateParams(lastSuccessParams.get())
        }
      })
  })

  const refreshList = () => loadList(params, null, true)

  useEffect(() => {
    loading.open()
    loadList(params, loading.close)
  }, [params])


  if (!worklist) {
    return <>
      <Fill name="QueryCount"><b>Loading...</b></Fill>
      <LoadingWorklist><Loading size={40} borderWidth={6} absolute/></LoadingWorklist>
    </>
  }
  const {worklist: {blankingWorklist, dbCount, rows}, key} = worklist

  return <>
    <Fill name="QueryCount"><b>{loading.isOpen ? 'Loading...' : <>{dbCount} {FILTER_LBABEL[allTabs ? params.studyStatus : ALL_CASES]} case{dbCount !== 1 ? "s" : ""}{params.search && " found"}</>}</b></Fill>
    <Fill name="Pagination"><Pagination
      setPageParam={(page) => () => setParams(_.set('page', page))}
      currentPage={currentPage}
      totalPages={Math.ceil((Number(dbCount)) / maxResults)}
    /></Fill>
    <Table
      key={key}
      blankingWorklist={blankingWorklist}
      refreshList={refreshList}
      isLoading={loading.isOpen}
      setParams={setParams} {...props}
      queryCount={Number(dbCount)}
      rows={rows}
    />
    {
      errorDialog.isOpen &&
      <FetchErrorDialog closeDialog={errorDialog.close}/>
    }
    <PaginationContainer><Slot name="Pagination"/></PaginationContainer>
  </>
})
